// src/stores/map/hooks/useHideConfirmationDebounced.ts
//
// Logic hook replacing the Redux thunks `hideWellConfirmationDebounced`
// and `hideFacilityConfirmationDebounced` from mapSlice.
//
// Returns debounced functions to hide well/facility confirmation dialogs.
// The 800ms delay matches the original Redux thunk behavior.
import { useEffect, useMemo } from "react";

import _debounce from "lodash/debounce";

import { useMapStore } from "../mapStore";

interface HideConfirmationDebounced {
  /** Hides the well series size confirmation dialog after 800ms. */
  readonly hideWellConfirmation: ReturnType<typeof _debounce>;
  /** Hides the facility series size confirmation dialog after 800ms. */
  readonly hideFacilityConfirmation: ReturnType<typeof _debounce>;
}

/**
 * Returns debounced functions to hide well/facility confirmation dialogs.
 * Replaces `hideWellConfirmationDebounced` and `hideFacilityConfirmationDebounced`
 * thunks from the Redux mapSlice.
 */
export function useHideConfirmationDebounced(): HideConfirmationDebounced {
  const setHasToConfirmSize = useMapStore((s) => s.setHasToConfirmSize);
  const setFacilityHasToConfirmSize = useMapStore((s) => s.setFacilityHasToConfirmSize);

  const hideWellConfirmation = useMemo(
    () => _debounce(() => setHasToConfirmSize(false), 800),
    [setHasToConfirmSize]
  );

  const hideFacilityConfirmation = useMemo(
    () => _debounce(() => setFacilityHasToConfirmSize(false), 800),
    [setFacilityHasToConfirmSize]
  );

  useEffect(
    () => () => {
      hideWellConfirmation.cancel();
      hideFacilityConfirmation.cancel();
    },
    [hideWellConfirmation, hideFacilityConfirmation]
  );

  return { hideWellConfirmation, hideFacilityConfirmation };
}
